import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Search, Filter, Calendar, BarChart3, Activity, List, ChevronDown, ChevronUp } from 'lucide-react';
import AppHeader from '../../components/AppHeader';
import BottomNav from '../../components/BottomNav';
import ErrorPage from '../../components/ErrorPage';
import { useNotification } from '../../context/NotificationContext';

const api = axios.create({ baseURL: process.env.REACT_APP_API_URL || 'https://drvivara.cloud/api' });
api.interceptors.request.use(c => { const t = localStorage.getItem('token') || sessionStorage.getItem('token'); if (t) c.headers.Authorization = `Bearer ${t}`; return c; });

const ACTION_COLORS = {
  create: 'bg-green-900/50 text-green-300', update: 'bg-blue-900/50 text-blue-300',
  delete: 'bg-red-900/50 text-red-300', login: 'bg-purple-900/50 text-purple-300',
  logout: 'bg-gray-700 text-gray-300', view: 'bg-cyan-900/50 text-cyan-300',
};

const AuditLog = () => {
  const { showError } = useNotification();
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({ action: '', targetModel: '', startDate: '', endDate: '' });
  const [showFilters, setShowFilters] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const fetchLogs = async (p = page) => {
    setLoading(true); setError(null);
    try {
      const params = new URLSearchParams();
      if (filters.action) params.set('action', filters.action);
      if (filters.targetModel) params.set('targetModel', filters.targetModel);
      if (filters.startDate) params.set('startDate', filters.startDate);
      if (filters.endDate) params.set('endDate', filters.endDate);
      params.set('page', p); params.set('limit', '20');
      const res = await api.get(`/admin/audit-logs?${params}`);
      setLogs(res.data.data?.logs || res.data.data || []);
      setTotalPages(res.data.data?.totalPages || res.data.pagination?.totalPages || 1);
    } catch (err) { setError(err.response?.data?.message || 'Failed to load audit logs'); }
    finally { setLoading(false); }
  };

  const fetchStats = async () => {
    try {
      const res = await api.get('/admin/audit-logs/stats');
      setStats(res.data.data);
    } catch {}
  };

  useEffect(() => { fetchLogs(1); setPage(1); }, [filters]);
  useEffect(() => { fetchStats(); }, []);

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) return;
    setPage(newPage);
    fetchLogs(newPage);
  };

  if (error) return <ErrorPage message={error} onRetry={() => fetchLogs(page)} />;

  const maxActionCount = stats?.actionSummary ? Math.max(...stats.actionSummary.map(a => a.count), 1) : 1;

  return (
    <div className="bg-dark min-h-screen flex flex-col">
      <AppHeader title="Audit Logs" />
      <div className="flex-1 p-4 pb-24 overflow-y-auto">
        {stats && (
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="bg-gray-800 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold text-gold">{stats.totalLogs || 0}</p>
              <p className="text-xs text-gray-400">Total</p>
            </div>
            <div className="bg-gray-800 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold text-blue-400">{stats.last24h || 0}</p>
              <p className="text-xs text-gray-400">Last 24h</p>
            </div>
            <div className="bg-gray-800 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold text-purple-400">{stats.uniqueAdmins || 0}</p>
              <p className="text-xs text-gray-400">Admins</p>
            </div>
          </div>
        )}

        {stats?.actionSummary && (
          <div className="bg-gray-800 rounded-xl p-4 mb-4">
            <h4 className="text-white text-sm font-bold mb-3 flex items-center gap-1"><BarChart3 size={14} /> Action Summary</h4>
            <div className="space-y-2">
              {stats.actionSummary.map(a => (
                <div key={a._id} className="flex items-center gap-2">
                  <span className="text-xs text-gray-400 w-16 capitalize">{a._id}</span>
                  <div className="flex-1 bg-gray-700 rounded-full h-3 overflow-hidden">
                    <div className="h-full bg-gold rounded-full transition-all" style={{ width: `${(a.count / maxActionCount) * 100}%` }} />
                  </div>
                  <span className="text-xs text-white font-medium w-8 text-right">{a.count}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {stats?.dailySummary && stats.dailySummary.length > 0 && (
          <div className="bg-gray-800 rounded-xl p-4 mb-4">
            <h4 className="text-white text-sm font-bold mb-3 flex items-center gap-1"><Activity size={14} /> Daily Activity (30 days)</h4>
            <div className="flex items-end gap-1 h-20">
              {stats.dailySummary.slice(-30).map((d, i) => (
                <div key={i} className="flex-1 flex flex-col items-center">
                  <div className="w-full bg-gold/80 rounded-t" style={{ height: `${Math.min((d.count / Math.max(...stats.dailySummary.map(x => x.count), 1)) * 64, 64)}px` }} title={`${d._id}: ${d.count}`} />
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex items-center justify-between mb-3">
          <h3 className="text-white font-bold text-sm">Log Entries</h3>
          <button onClick={() => setShowFilters(!showFilters)} className="text-gold text-xs flex items-center gap-1">
            <Filter size={14} /> Filters {showFilters ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
        </div>

        {showFilters && (
          <div className="bg-gray-800 rounded-xl p-4 mb-4">
            <div className="grid grid-cols-2 gap-3">
              <select value={filters.action} onChange={e => setFilters(f => ({ ...f, action: e.target.value }))} className="bg-gray-700 text-white rounded-lg p-2 text-xs border border-gray-600">
                <option value="">All Actions</option>
                <option value="create">Create</option>
                <option value="update">Update</option>
                <option value="delete">Delete</option>
                <option value="login">Login</option>
                <option value="logout">Logout</option>
                <option value="view">View</option>
              </select>
              <select value={filters.targetModel} onChange={e => setFilters(f => ({ ...f, targetModel: e.target.value }))} className="bg-gray-700 text-white rounded-lg p-2 text-xs border border-gray-600">
                <option value="">All Models</option>
                <option value="User">User</option>
                <option value="Appointment">Appointment</option>
                <option value="MedicalRecord">Medical Record</option>
                <option value="Reel">Reel</option>
                <option value="InventoryItem">Inventory</option>
                <option value="EmergencyRequest">Emergency</option>
                <option value="Bed">Bed</option>
                <option value="Department">Department</option>
              </select>
              <input type="date" value={filters.startDate} onChange={e => setFilters(f => ({ ...f, startDate: e.target.value }))} className="bg-gray-700 text-white rounded-lg p-2 text-xs border border-gray-600" placeholder="Start date" />
              <input type="date" value={filters.endDate} onChange={e => setFilters(f => ({ ...f, endDate: e.target.value }))} className="bg-gray-700 text-white rounded-lg p-2 text-xs border border-gray-600" placeholder="End date" />
            </div>
          </div>
        )}

        <div className="space-y-2">
          {loading ? (
            <p className="text-center text-gray-500 py-8">Loading...</p>
          ) : logs.length > 0 ? (
            logs.map(log => (
              <div key={log._id} className="bg-gray-800 rounded-xl overflow-hidden">
                <button onClick={() => setExpandedId(expandedId === log._id ? null : log._id)} className="w-full p-3 flex items-start justify-between text-left">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs px-2 py-0.5 rounded font-medium capitalize ${ACTION_COLORS[log.action] || 'bg-gray-700 text-gray-400'}`}>{log.action}</span>
                      <span className="text-xs text-gray-500">{log.targetModel}</span>
                      {log.targetId && <span className="text-xs text-gray-600">ID: {log.targetId.slice(-6)}</span>}
                    </div>
                    <p className="text-white text-xs truncate">{log.admin?.name || log.admin?.email || 'System'} · {new Date(log.createdAt).toLocaleString()}</p>
                  </div>
                  <div className="shrink-0 ml-2">{expandedId === log._id ? <ChevronUp size={14} className="text-gray-500" /> : <ChevronDown size={14} className="text-gray-500" />}</div>
                </button>
                {expandedId === log._id && log.metadata && Object.keys(log.metadata).length > 0 && (
                  <div className="px-3 pb-3 border-t border-gray-700 pt-2">
                    <pre className="text-xs text-gray-400 overflow-x-auto">{JSON.stringify(log.metadata, null, 2)}</pre>
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="text-center text-gray-500 py-8"><List size={48} className="mx-auto mb-3 opacity-30" /><p>No logs found</p></div>
          )}
        </div>

        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-2 mt-4">
            <button disabled={page <= 1} onClick={() => handlePageChange(page - 1)} className="px-3 py-1.5 bg-gray-800 text-gray-400 rounded-lg text-xs disabled:opacity-30">Prev</button>
            <span className="text-xs text-gray-500">{page} / {totalPages}</span>
            <button disabled={page >= totalPages} onClick={() => handlePageChange(page + 1)} className="px-3 py-1.5 bg-gray-800 text-gray-400 rounded-lg text-xs disabled:opacity-30">Next</button>
          </div>
        )}
      </div>
      <BottomNav />
    </div>
  );
};

export default AuditLog;
